/* eslint-disable react/prop-types */
/*
 * /delete-account — public page where a customer asks Redbox to delete their account and personal data.
 * Required by Google Play for apps with user accounts (Data safety → "Delete account URL"), and linked
 * from the personal area (Profile → "מחיקת חשבון").
 *
 * The request is sent through the existing contact-us endpoint (opcode addContactUs): it is saved in the
 * CONTACT_US table (admin → contactUsList) and emailed to Redbox, with the subject "בקשה למחיקת חשבון".
 * Handling is manual — see docs/mobile/ACCOUNT_DELETION.md.
 */
import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '../../style-guide';
import ActionButton from '../Common/ActionButton';
import Loader from '../../style-guide/Loader';
import { snackbarToggle } from '../../reducers/snackbarSlicer';
import { addContactUs, validateEmail, validatePhone } from '../../reducers/requestHandler';
import './DeleteAccount.scss';

export const DELETE_ACCOUNT_SUBJECT = 'בקשה למחיקת חשבון';
const SUPPORT_EMAIL = 'info@redboxparcel.com';

const DeleteAccount = () => {
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.user) || {};
    const user = userInfo?.user || {};
    const [data, setData] = useState({
        fullName: [user.firstName, user.lastName].filter(Boolean).join(' '),
        email: user.email || '',
        phone: user.phone || '',
        boxNumber: user.token || '',
        reason: '',
        confirm: false,
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (key) => (event) => setData(prev => ({ ...prev, [key]: event.target.value }));
    const valid = data.fullName.trim().length > 1 && validateEmail(data.email) && validatePhone(data.phone) && data.confirm;

    const submit = () => {
        if (!valid) {
            dispatch(snackbarToggle({ type: 'error', message: 'נא למלא שם, אימייל וטלפון תקינים ולאשר את הבקשה' }));
            return;
        }
        const content = [
            'בקשה למחיקת חשבון ונתונים אישיים / Account deletion request',
            `מספר RBP: ${data.boxNumber || '-'}`,
            `מזהה משתמש: ${user.id || '-'}`,
            `סיבה: ${data.reason || '-'}`,
            `נשלח מ: ${/RedboxApp\//.test(navigator.userAgent) ? 'אפליקציה' : 'אתר'}`,
        ].join('\n');
        setLoading(true);
        addContactUs({
            sessionId: userInfo?.session,
            userId: user.id,
            fullName: data.fullName.trim(),
            email: data.email.trim(),
            phone: data.phone.trim(),
            subject: DELETE_ACCOUNT_SUBJECT,
            content,
        }, (response) => {
            setLoading(false);
            if (response?.misc?.CODE === 1) {
                setSent(true);
            } else {
                dispatch(snackbarToggle({ type: 'error', message: `השליחה נכשלה. אפשר לשלוח את הבקשה במייל ל-${SUPPORT_EMAIL}` }));
            }
        });
    };

    return (
        <Box className="delete_account_page">
            {loading && <Loader />}
            <h1>מחיקת חשבון Redbox Parcel</h1>
            <p>
                בעמוד זה ניתן לבקש למחוק את החשבון שלכם באתר ובאפליקציית Redbox Parcel (חברת רד בוקס פרסל, קבוצת Total Care Logistics),
                יחד עם הפרטים האישיים השמורים בו.
            </p>

            <h2>איך מבקשים</h2>
            <ol>
                <li>ממלאים את הטופס שבהמשך (מומלץ עם כתובת האימייל של החשבון), או</li>
                <li>שולחים מייל מכתובת האימייל של החשבון אל <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(DELETE_ACCOUNT_SUBJECT)}`}>{SUPPORT_EMAIL}</a> בנושא "{DELETE_ACCOUNT_SUBJECT}", עם השם המלא ומספר ה-RBP.</li>
            </ol>
            <p>נאמת שהבקשה הגיעה מבעל החשבון, ונאשר במייל כשהמחיקה תושלם — תוך 30 יום לכל היותר.</p>

            <h2>מה נמחק</h2>
            <ul>
                <li>החשבון והגישה אליו (שם משתמש וסיסמה, כניסה בטביעת אצבע או זיהוי פנים)</li>
                <li>פרטים אישיים: שם, אימייל, טלפון, כתובות, תאריך לידה ומספר זהות או ח.פ.</li>
                <li>כתובות המשלוח בחו"ל, אמצעי תשלום שמורים, קבצים שהועלו, והרשמה לדיוור והתראות</li>
            </ul>

            <h2>מה נשמר</h2>
            <p>
                רשומות שהחוק מחייב לשמור — משלוחים, הצהרות ושחרור מכס, חשבוניות ותשלומים — נשמרות לתקופה הנדרשת על פי דין
                (בדרך כלל עד 7 שנים) ונמחקות בתומה. משלוח שנמצא בדרך יטופל עד למסירתו לפני מחיקת החשבון.
            </p>

            {sent ? (
                <Box className="delete_account_done">
                    הבקשה התקבלה. נחזור אליכם במייל לאישור המחיקה.
                </Box>
            ) : (
                <Box className="delete_account_form">
                    <h2>טופס בקשה</h2>
                    <TextField className='text-field' required label='שם מלא' dir='rtl' value={data.fullName} onChange={set('fullName')} />
                    <TextField className='text-field' required label='אימייל של החשבון' dir='rtl' value={data.email} onChange={set('email')} />
                    <TextField className='text-field' required label='טלפון' dir='rtl' type='phone' value={data.phone} onChange={set('phone')} />
                    <TextField className='text-field' label='מספר RBP (אם ידוע)' dir='rtl' value={data.boxNumber} onChange={set('boxNumber')} />
                    <TextField className='text-field' label='סיבה (לא חובה)' dir='rtl' value={data.reason} onChange={set('reason')} />
                    <FormControlLabel
                        control={<Checkbox checked={data.confirm} onChange={e => setData(prev => ({ ...prev, confirm: e.target.checked }))} />}
                        label='אני מבקש/ת למחוק את החשבון שלי ואת הפרטים האישיים, ומבין/ה שלא ניתן לשחזר אותם'
                    />
                    <ActionButton className='submit-action' textLabel='שליחת בקשה למחיקה' disabled={!valid} onClick={submit} />
                </Box>
            )}

            <Box className="delete_account_en" dir="ltr">
                <h2>Delete your Redbox Parcel account</h2>
                <p>
                    To delete your Redbox Parcel account (website and app) and its personal data, fill in the form above or email{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Account deletion request')}`}>{SUPPORT_EMAIL}</a>{' '}
                    from your account's email address with the subject "Account deletion request", your full name and RBP number.
                    We confirm by email when it is done, within 30 days.
                </p>
                <p>
                    <b>Deleted:</b> your account and login, name, email, phone, addresses, date of birth, ID or company number,
                    overseas shipping addresses, saved payment methods, uploaded files, newsletter and notification subscriptions.
                </p>
                <p>
                    <b>Kept:</b> records the law requires us to keep — shipments, customs declarations and releases, invoices and
                    payments — for the legally required period (usually up to 7 years), then deleted.
                </p>
            </Box>
        </Box>
    );
};

export default DeleteAccount;
